import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MessageSquare, Loader2, Send } from 'lucide-react';
import { InviteShareActions, DeliveryChips } from './InviteShareActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const font = { fontFamily: 'IBM Plex Sans, sans-serif' };
const label = 'block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2';
const input = 'w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none';

// Invite a driver by email and/or text. Server sends the branded message; admin can also
// share the link from their own phone (works inside the native app — no sign-up needed).
export const DriverInviteForm = ({ companyName, senderName, onInvited }) => {
  const [form, setForm] = useState({ email: '', name: '', phone: '', send_email: true, send_sms: false });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.send_sms && !form.phone.trim()) { setError('Add a mobile number to send a text invite.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/driver-invitations`, form, { withCredentials: true });
      setResult(res.data);
      const parts = [];
      if (form.send_email) parts.push(res.data.email_sent ? 'email sent' : 'email failed');
      if (form.send_sms) parts.push(res.data.sms_sent ? 'text sent' : `text failed${res.data.sms_error ? ` — ${res.data.sms_error.slice(0, 80)}` : ''}`);
      (res.data.email_sent || res.data.sms_sent ? toast.success : toast.warning)(`Invitation created${parts.length ? ` · ${parts.join(' · ')}` : ''}`);
      setForm({ email: '', name: '', phone: '', send_email: true, send_sms: false });
      onInvited?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to send invitation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 mb-6" data-testid="driver-invite-form">
      <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Invite a Driver</h2>
      <p className="text-xs text-slate-500 mb-4" style={font}>The driver gets a one-tap link — no sign-up, no password. We send it for you by email and/or text, or share it from your own phone.</p>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={label} style={font}>Driver Email</label>
            <input type="email" data-testid="invite-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={font} required />
          </div>
          <div>
            <label className={label} style={font}>Mobile Number (for text)</label>
            <input type="tel" data-testid="invite-phone-input" placeholder="+1 555 123 4567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} style={font} />
          </div>
          <div>
            <label className={label} style={font}>Driver Name (optional)</label>
            <input type="text" data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} style={font} />
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-slate-700" style={font}>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="invite-send-email-checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="rounded border-slate-300" />
            <Mail className="w-4 h-4 text-slate-500" /> Send by email
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="invite-send-sms-checkbox" checked={form.send_sms} onChange={(e) => setForm({ ...form, send_sms: e.target.checked })} className="rounded border-slate-300" />
            <MessageSquare className="w-4 h-4 text-slate-500" /> Send by text message
          </label>
        </div>
        {error && <div data-testid="invite-error-message" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{error}</div>}
        <button type="submit" disabled={busy} data-testid="send-invite-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-50" style={font}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2} />}
          {form.send_email || form.send_sms ? 'Send Invite' : 'Create Invite Link'}
        </button>
      </form>

      {result && (
        <div data-testid="invite-link-result" className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={font}>Invitation for {result.name || result.email}</p>
            <DeliveryChips invite={result} />
          </div>
          {result.sms_error && result.phone && (
            <p className="text-xs text-amber-800" data-testid="invite-sms-error" style={font}>Text not delivered: {result.sms_error}. Use "My phone" or "Share…" below to send it yourself.</p>
          )}
          <input type="text" value={result.invite_link} readOnly data-testid="invite-link-input" className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-xs font-mono" />
          <InviteShareActions invite={result} companyName={companyName} senderName={senderName} />
        </div>
      )}
    </div>
  );
};
